import {
  ArrowLeftIcon,
  ClipboardIcon,
  EnvelopeIcon,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Divider } from '@heroui/divider';
import { Input, Textarea } from '@heroui/input';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/modal';
import { Select, SelectItem } from '@heroui/select';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import OrderBoardComponent from '../../components/OrderBoard/OrderBoard';
import SEOHead from '../../components/SEOHead';
import { useLocale } from '../../contexts/LocaleContext';
import { adminService } from '../../services/adminService';
import { orderBoardService } from '../../services/orderBoardService';
import { orderService } from '../../services/orderService';
import { AdminOrderDetail as AdminOrderDetailType } from '../../types/admin';
import { OrderBoard } from '../../types/orderBoard';
import { OrderStatusObject } from '../../types/orders';
import { generateCanonicalUrl } from '../../utils/seoUtils';

const AdminOrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<AdminOrderDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [orderStatuses, setOrderStatuses] = useState<OrderStatusObject[]>([]);
  const [productBoards, setProductBoards] = useState<OrderBoard[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [isCreatingBoards, setIsCreatingBoards] = useState(false);
  const { formatCurrency, convertPrice } = useLocale();
  const [updateForm, setUpdateForm] = useState({
    status: '',
    trackingNumber: '',
    notes: '',
  });

  const {
    isOpen: isUpdateOpen,
    onOpen: onUpdateOpen,
    onClose: onUpdateClose,
  } = useDisclosure();

  const formatAmount = (amount: number) => formatCurrency(convertPrice(amount));

  const loadProductBoards = useCallback(async () => {
    if (!orderId) return;
    try {
      setIsLoadingBoards(true);
      const boards = await orderBoardService.getBoardsByOrderId(
        Number(orderId)
      );
      setProductBoards(boards);
      if (boards.length > 0 && !selectedBoardId) {
        setSelectedBoardId(boards[0].id);
      }
    } catch (error: any) {
      // Don't show error toast - boards may not exist yet
    } finally {
      setIsLoadingBoards(false);
    }
  }, [orderId, selectedBoardId]);

  const handleCreateProductBoards = async () => {
    if (!orderId) return;

    try {
      setIsCreatingBoards(true);
      const boards = await orderBoardService.createBoardsForOrder(
        Number(orderId)
      );
      toast.success(
        `Successfully created ${boards.length} product board${boards.length !== 1 ? 's' : ''}`
      );
      // Reload boards to get the updated list
      await loadProductBoards();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Failed to create product boards'
      );
    } finally {
      setIsCreatingBoards(false);
    }
  };

  const fetchOrderStatuses = useCallback(async () => {
    try {
      const statuses = await orderService.getOrderStatuses();
      setOrderStatuses(statuses);
    } catch (error) {
      toast.error('Failed to load order statuses');
    }
  }, []);

  const loadOrder = useCallback(async () => {
    try {
      setIsLoading(true);
      const orderData = await adminService.getAdminOrder(Number(orderId));
      setOrder(orderData);
      setUpdateForm({
        status: orderData.status,
        trackingNumber: orderData.trackingNumber || '',
        notes: orderData.notes || '',
      });
    } catch (error: any) {
      toast.error('Failed to load order details');
      navigate('/admin/orders');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, orderId]);

  useEffect(() => {
    fetchOrderStatuses();
    if (orderId) {
      loadOrder();
      loadProductBoards();
    }
  }, [fetchOrderStatuses, loadOrder, loadProductBoards, orderId]);

  const handleUpdateOrder = () => {
    if (!order) return;
    onUpdateOpen();
  };

  const submitOrderUpdate = async () => {
    if (!order) return;

    try {
      await adminService.updateOrderStatus(order.id, updateForm);
      toast.success('Order updated successfully');
      onUpdateClose();
      loadOrder();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update order');
    }
  };

  const sendStatusEmail = async () => {
    if (!order) return;

    try {
      await adminService.sendOrderStatusEmail(order.id);
      toast.success('Status email sent successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send email');
    }
  };

  const printShippingLabel = () => {
    // Placeholder for printing functionality
    toast.success('Printing functionality will be implemented soon');
  };

  const copyOrderNumber = () => {
    if (order) {
      navigator.clipboard.writeText(order.orderNumber);
      toast.success('Order number copied to clipboard');
    }
  };

  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'primary';
      case 'printing':
        return 'secondary';
      case 'printed':
        return 'secondary';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'refunded':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'primary';
      case 'paid':
        return 'success';
      case 'failed':
        return 'danger';
      case 'refunded':
        return 'danger';
      case 'cancelled':
        return 'danger';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='animate-pulse'>
          <div className='h-8 bg-gray-300 rounded w-1/4 mb-6' />
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
            <div className='lg:col-span-2 space-y-6'>
              <div className='h-64 bg-gray-300 rounded-lg' />
              <div className='h-48 bg-gray-300 rounded-lg' />
            </div>
            <div className='h-64 bg-gray-300 rounded-lg' />
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className='text-center py-12'>
        <p className='text-gray-500 mb-4'>Order not found</p>
        <Button as={Link} to='/admin/orders' color='primary'>
          Back to Orders
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <SEOHead
        title={`Admin Order ${order.orderNumber}`}
        description={`Manage order ${order.orderNumber} - status ${order.status}, payment ${order.paymentStatus}.`}
        url={generateCanonicalUrl(`/admin/orders/${order.id}`)}
        type='website'
      />
      {/* Header */}
      <div className='md:flex md:items-center md:justify-between'>
        <div className='flex-1 min-w-0'>
          <nav className='flex' aria-label='Breadcrumb'>
            <ol className='flex items-center space-x-4'>
              <li>
                <Link
                  to='/admin/orders'
                  className='text-gray-400 hover:text-gray-500'
                >
                  <ArrowLeftIcon
                    className='flex-shrink-0 h-5 w-5'
                    aria-hidden='true'
                  />
                </Link>
              </li>
              <li>
                <div className='flex items-center'>
                  <span className='text-gray-400'>/</span>
                  <span className='ml-4 text-sm font-medium text-gray-500'>
                    Orders
                  </span>
                </div>
              </li>
              <li>
                <div className='flex items-center'>
                  <span className='text-gray-400'>/</span>
                  <span className='ml-4 text-sm font-medium text-gray-900'>
                    {order.orderNumber}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
          <h1 className='mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate'>
            Order {order.orderNumber}
          </h1>
          <div className='mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6'>
            <div className='mt-2 flex items-center text-sm text-gray-500'>
              <p>
                Order placed on {new Date(order.orderDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
        <div className='mt-4 flex space-x-2 md:mt-0 md:ml-4'>
          <Button
            onClick={copyOrderNumber}
            variant='ghost'
            size='sm'
            startContent={<ClipboardIcon className='h-4 w-4' />}
          >
            Copy Order #
          </Button>
          <Button
            onClick={printShippingLabel}
            variant='ghost'
            size='sm'
            startContent={<PrinterIcon className='h-4 w-4' />}
          >
            Print Label
          </Button>
          <Button
            onClick={sendStatusEmail}
            variant='ghost'
            size='sm'
            startContent={<EnvelopeIcon className='h-4 w-4' />}
          >
            Send Email
          </Button>
          <Button
            onClick={handleUpdateOrder}
            color='primary'
            size='sm'
            startContent={<PencilIcon className='h-4 w-4' />}
          >
            Update Status
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Order Items */}
        <div className='lg:col-span-2 space-y-6'>
          <Card>
            <CardHeader>
              <h3 className='text-lg font-medium'>Order Items</h3>
            </CardHeader>
            <CardBody>
              <div className='space-y-4'>
                {order.items.map(item => (
                  <div
                    key={item.id}
                    className='flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0'
                  >
                    <div className='flex-1'>
                      <h4 className='text-sm font-medium text-gray-900'>
                        {item.productName}
                      </h4>
                      <p className='text-sm text-gray-500'>
                        Product ID: {item.productId}
                      </p>
                      <p className='text-sm text-gray-500'>
                        Unit Price: {formatAmount(item.price)}
                      </p>
                    </div>
                    <div className='text-right'>
                      <p className='text-sm font-medium text-gray-900'>
                        Qty: {item.quantity}
                      </p>
                      <p className='text-sm font-medium text-gray-900'>
                        {formatAmount(item.totalPrice)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* Shipping Address */}
          <Card>
            <CardHeader>
              <h3 className='text-lg font-medium'>Shipping Address</h3>
            </CardHeader>
            <CardBody>
              <div className='text-sm'>
                <p className='font-medium'>{order.shippingName}</p>
                <p>{order.shippingAddress}</p>
                <p>
                  {order.shippingCity}, {order.shippingState}{' '}
                  {order.shippingZipCode}
                </p>
                <p>{order.shippingCountry}</p>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Order Summary */}
        <div className='space-y-6'>
          <Card>
            <CardHeader>
              <h3 className='text-lg font-medium'>Order Summary</h3>
            </CardHeader>
            <CardBody className='space-y-4'>
              <div className='flex items-center justify-between'>
                <span className='text-sm'>Status:</span>
                <Chip color={getStatusColor(order.status)} size='sm'>
                  {order.status}
                </Chip>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm'>Payment:</span>
                <Chip
                  color={getPaymentStatusColor(order.paymentStatus)}
                  size='sm'
                >
                  {order.paymentStatus}
                </Chip>
              </div>
              {order.trackingNumber && (
                <div className='flex items-center justify-between'>
                  <span className='text-sm'>Tracking:</span>
                  <span className='text-sm font-medium'>
                    {order.trackingNumber}
                  </span>
                </div>
              )}
              <Divider />
              <div className='space-y-2'>
                <div className='flex justify-between text-sm'>
                  <span>Subtotal:</span>
                  <span>
                    {formatAmount(
                      order.subtotalAmount ??
                        order.totalAmount - order.shippingCost - order.taxAmount
                    )}
                  </span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span>Shipping:</span>
                  <span>{formatAmount(order.shippingCost)}</span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span>Tax:</span>
                  <span>{formatAmount(order.taxAmount)}</span>
                </div>
                <Divider />
                <div className='flex justify-between font-semibold'>
                  <span>Total:</span>
                  <span>{formatAmount(order.totalAmount)}</span>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Customer Info */}
          <Card>
            <CardHeader>
              <h3 className='text-lg font-medium'>Customer Information</h3>
            </CardHeader>
            <CardBody className='space-y-2'>
              <div>
                <p className='text-sm font-medium'>{order.customerName}</p>
                <p className='text-sm text-gray-500'>{order.customerEmail}</p>
              </div>
            </CardBody>
          </Card>

          {/* Order Timeline */}
          <Card>
            <CardHeader>
              <h3 className='text-lg font-medium'>Order Timeline</h3>
            </CardHeader>
            <CardBody className='space-y-3'>
              <div className='flex items-start space-x-3'>
                <div className='flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2' />
                <div>
                  <p className='text-sm font-medium'>Order placed</p>
                  <p className='text-xs text-gray-500'>
                    {new Date(order.orderDate).toLocaleString()}
                  </p>
                </div>
              </div>
              {order.shippedDate && (
                <div className='flex items-start space-x-3'>
                  <div className='flex-shrink-0 w-2 h-2 bg-success rounded-full mt-2' />
                  <div>
                    <p className='text-sm font-medium'>Shipped</p>
                    <p className='text-xs text-gray-500'>
                      {new Date(order.shippedDate).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
              {order.deliveredDate && (
                <div className='flex items-start space-x-3'>
                  <div className='flex-shrink-0 w-2 h-2 bg-success rounded-full mt-2' />
                  <div>
                    <p className='text-sm font-medium'>Delivered</p>
                    <p className='text-xs text-gray-500'>
                      {new Date(order.deliveredDate).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Product Boards Section */}
      {order.items.length > 0 && (
        <div className='space-y-6'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between'>
              <div>
                <h3 className='text-lg font-medium'>Product Boards</h3>
                <p className='text-sm text-gray-500'>
                  Each product in this order has its own workflow board
                </p>
              </div>
              {productBoards.length === 0 && (
                <Button
                  color='primary'
                  size='sm'
                  startContent={<PlusIcon className='h-4 w-4' />}
                  onClick={handleCreateProductBoards}
                  isLoading={isCreatingBoards}
                  disabled={isCreatingBoards}
                >
                  Create Product Boards
                </Button>
              )}
            </CardHeader>
            <CardBody>
              {isLoadingBoards ? (
                <div className='text-center py-8'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto' />
                  <p className='mt-2 text-sm text-gray-500'>
                    Loading boards...
                  </p>
                </div>
              ) : productBoards.length === 0 ? (
                <div className='text-center py-8 text-gray-500'>
                  <p className='text-sm'>
                    No product boards found for this order.
                  </p>
                  <p className='text-xs mt-1 mb-4'>
                    Boards will be created automatically when orders are placed,
                    or you can create them manually.
                  </p>
                  <Button
                    color='primary'
                    size='sm'
                    startContent={<PlusIcon className='h-4 w-4' />}
                    onClick={handleCreateProductBoards}
                    isLoading={isCreatingBoards}
                    disabled={isCreatingBoards}
                  >
                    Create Product Boards
                  </Button>
                </div>
              ) : (
                <div className='space-y-6'>
                  {productBoards.length > 1 && (
                    <div className='flex gap-2 flex-wrap'>
                      {productBoards.map(board => (
                        <Button
                          key={board.id}
                          variant={
                            selectedBoardId === board.id ? 'solid' : 'flat'
                          }
                          color={
                            selectedBoardId === board.id ? 'primary' : 'default'
                          }
                          size='sm'
                          onClick={() => setSelectedBoardId(board.id)}
                        >
                          {board.productName || `Board ${board.id}`}
                          {board.isLocked && ' 🔒'}
                        </Button>
                      ))}
                    </div>
                  )}
                  {selectedBoardId && (
                    <div>
                      {productBoards
                        .filter(b => b.id === selectedBoardId)
                        .map(board => (
                          <div key={board.id} className='space-y-4'>
                            <div className='flex items-center justify-between'>
                              <div>
                                <h4 className='font-semibold text-gray-900'>
                                  {board.productName || board.title}
                                </h4>
                                {board.isLocked && (
                                  <p className='text-sm text-red-600 font-medium'>
                                    🔒 Board is locked (order shipped or
                                    delivered)
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className='bg-gray-50 p-4 rounded-lg border border-gray-200'>
                              <OrderBoardComponent
                                board={board}
                                onBoardUpdate={loadProductBoards}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Update Order Modal */}
      <Modal isOpen={isUpdateOpen} onClose={onUpdateClose} size='2xl'>
        <ModalContent>
          <ModalHeader>
            <h3>Update Order Status</h3>
          </ModalHeader>
          <ModalBody className='space-y-4'>
            <Select
              label='Status'
              selectedKeys={[updateForm.status]}
              items={orderStatuses}
              onSelectionChange={keys => {
                const selectedKey = Array.from(keys)[0] as string;
                setUpdateForm(prev => ({ ...prev, status: selectedKey }));
              }}
            >
              {status => <SelectItem key={status.id}>{status.name}</SelectItem>}
            </Select>
            <Input
              label='Tracking Number'
              value={updateForm.trackingNumber}
              onChange={e =>
                setUpdateForm(prev => ({
                  ...prev,
                  trackingNumber: e.target.value,
                }))
              }
              placeholder='Enter tracking number (optional)'
            />
            <Textarea
              label='Notes'
              value={updateForm.notes}
              onChange={e =>
                setUpdateForm(prev => ({ ...prev, notes: e.target.value }))
              }
              placeholder='Add any notes about this order...'
              minRows={3}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant='ghost' onClick={onUpdateClose}>
              Cancel
            </Button>
            <Button color='primary' onClick={submitOrderUpdate}>
              Update Order
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AdminOrderDetail;
